import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { IUserModel } from '@packages/global';

interface CompactModelSelectorProps {
  label: string;
  models: IUserModel[];
  value: string;
  onChange: (modelId: string) => void;
  disabled?: boolean;
  direction?: 'up' | 'down'; // 弹出方向
}

export default function CompactModelSelector({
  label,
  models,
  value,
  onChange,
  disabled = false,
  direction = 'down'
}: CompactModelSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedModel = models.find(m => m.id === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (modelId: string) => {
    onChange(modelId);
    setIsOpen(false);
  };

  const dropdownPositionClass = direction === 'up'
    ? 'bottom-full mb-1'
    : 'top-full mt-1';

  return (
    <div ref={dropdownRef}>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full flex items-center justify-between px-2 py-1.5 bg-bg-elevated text-sm rounded border transition-colors ${isOpen ? 'border-primary' : 'border-border-subtle hover:border-border-default'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <span className={`truncate ${selectedModel ? 'text-gray-300' : 'text-gray-500'}`}>
            {selectedModel?.title || '选择模型'}
          </span>
          <ChevronDownIcon className={`w-3 h-3 text-gray-400 flex-shrink-0 ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && models.length > 0 && (
          <div className={`absolute ${dropdownPositionClass} left-0 right-0 bg-bg-elevated border border-border-subtle rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto`}>
            {models.map(model => (
              <button
                key={model.id}
                type="button"
                onClick={() => handleSelect(model.id)}
                className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-bg-surface transition-colors ${value === model.id ? 'text-primary bg-bg-surface/50' : 'text-gray-300'
                  }`}
              >
                <span className="truncate">{model.title}</span>
                {value === model.id && (
                  <CheckIcon className="w-4 h-4 text-primary flex-shrink-0 ml-2" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
